import { useState, useCallback, useEffect, useRef } from "react";
import { minutesToMilliseconds } from "date-fns";
import { useIdleTimer } from "react-idle-timer";
import { LAST_ACTIVITY_AT_STORAGE_KEY } from "../constants/sign-out-storage-keys";

const FORTY_FIVE_MINUTES_MS = minutesToMilliseconds(45);
const ONE_MINUTE_MS = minutesToMilliseconds(1);

interface UseAutoLogoutProps {
  onLogout: () => void;
  onInactivityLogout?: () => void;
  isAuthenticated: boolean;
}

function readLastActivityAt(): number | null {
  const raw = localStorage.getItem(LAST_ACTIVITY_AT_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
}

function writeLastActivityAt(): void {
  localStorage.setItem(LAST_ACTIVITY_AT_STORAGE_KEY, String(Date.now()));
}

export function useAutoLogout({
  onLogout,
  onInactivityLogout,
  isAuthenticated,
}: UseAutoLogoutProps) {
  const [showWarning, setShowWarning] = useState(false);
  const hasRunMountCheckRef = useRef(false);

  const handleIdle = useCallback(() => {
    setShowWarning(false);
    onInactivityLogout?.();
    onLogout();
  }, [onLogout, onInactivityLogout]);

  const handlePrompt = useCallback(() => {
    setShowWarning(true);
  }, []);

  const handleActivity = useCallback(() => {
    setShowWarning(false);
  }, []);

  const handleAction = useCallback(() => {
    if (isAuthenticated) {
      writeLastActivityAt();
    }
  }, [isAuthenticated]);

  const { activate } = useIdleTimer({
    timeout: FORTY_FIVE_MINUTES_MS,
    promptBeforeIdle: ONE_MINUTE_MS,
    onIdle: handleIdle,
    onPrompt: handlePrompt,
    onActive: handleActivity,
    onAction: handleAction,
    throttle: 500,
    eventsThrottle: 200,
    crossTab: true,
    syncTimers: 500,
  });

  useEffect(() => {
    if (!isAuthenticated || hasRunMountCheckRef.current) {
      return;
    }
    hasRunMountCheckRef.current = true;

    const lastActivityAt = readLastActivityAt();
    if (lastActivityAt === null) {
      writeLastActivityAt();
      return;
    }

    const diff = Date.now() - lastActivityAt;
    const isStale = diff < 0 || diff > FORTY_FIVE_MINUTES_MS;
    if (isStale) {
      onInactivityLogout?.();
      onLogout();
      return;
    }

    writeLastActivityAt();
  }, [isAuthenticated, onLogout, onInactivityLogout]);

  const handleStayLoggedIn = useCallback(() => {
    setShowWarning(false);
    if (isAuthenticated) {
      writeLastActivityAt();
    }
    activate();
  }, [activate, isAuthenticated]);

  return {
    showWarning,
    handleStayLoggedIn,
    handleActivity,
  };
}
